/**
 * cameraSeeThrough — fades environment Gaussian splats that sit between the
 * camera and the avatar head, so geometry doesn't occlude the face while
 * orbiting (camera-in-blueprint, see-through mode).
 *
 * THIS MODULE IS NOW THE PARAMETERS ONLY. It holds the config and the per-frame
 * camera/head/strength that `cameraController` feeds through a sink, and hands
 * them to whoever draws the environment via `readParams()`.
 *
 * It used to also BE the effect, as a Spark `worldModifier` — a per-Gaussian
 * dyno spliced into Spark's shader. That is gone, and it cost nothing: measured,
 * Spark's environment was BYTE-IDENTICAL with see-through enabled and disabled
 * (mean 0.00 over a wall crop), so the modifier had no visual effect on that path
 * at all. What replaced it does work, and there is ONE of it: `GaussianSplat`'s
 * opt-in `seeThrough` uniforms, driven by `ThreeEnvironmentSplat` for a GLB
 * character's room and by `ThreeSplatBridge` for the avatar's own mesh, which
 * exempts its face through the material's `slot0`. The bridge ran a second
 * implementation as a compute pass until that uniform existed.
 *
 * Ownership is unchanged: the camera singleton only forwards {camPos, headPos,
 * strength} each frame; nothing about the effect lives in `cameraController`.
 */

import * as THREE from 'three'
import { liveTuneStore } from './liveTuneStore.js'
import { cameraController } from './cameraController.js'

class CameraSeeThrough {
  constructor() {
    this._config = null
    // Plain holders, not Spark `dynoVec3`/`dynoFloat`. They were uniform nodes
    // because the dyno read them directly; every consumer now goes through
    // `readParams()`, so the `{ value }` shape is all that is left of that and
    // is kept only because the readers already expect it.
    this._camPos = { value: new THREE.Vector3() }
    this._headPos = { value: new THREE.Vector3() }
    // Global fade strength (0 = untouched, 1 = full fade). Driven per frame by
    // cameraController — 1 for manual see-through, or an eased 0<->1 for `auto`
    // (a proximity/angle heuristic).
    this._strength = { value: 1 }
    this._report('idle')
  }

  /** Take the config. THE ONE THAT MATTERS: with no config `readParams()` is
   *  inactive on every path, so the shader and the compute pass both fade
   *  nothing and neither reports anything wrong. Nobody calls this from a
   *  component any more — see `attach()`. */
  configure(config) {
    this._config = config
    this._report(config?.enabled || config?.auto ? 'applied' : 'idle')
  }

  /** Forget the config (env swap / teardown). */
  uninstall() {
    this._config = null
    this._report('idle')
  }

  /** Per-frame camera + head world positions (arrays [x,y,z]) + fade strength
   *  (0..1). Cheap uniform upload; no-op if never installed. */
  update(camPos, headPos, strength) {
    if (camPos) this._camPos.value.set(camPos[0], camPos[1], camPos[2])
    if (headPos) this._headPos.value.set(headPos[0], headPos[1], headPos[2])
    if (strength !== undefined && strength !== null) this._strength.value = strength
  }


  /** The fade's live parameters. Both meshes read them from here and hand them to
   *  the same `setSeeThrough`, so they cannot disagree about the volume being
   *  faded. */
  readParams() {
    const cfg = this._config ?? {}
    const on = !!(cfg.enabled || cfg.auto)
    const c = this._camPos.value, h = this._headPos.value
    return {
      active: on && this._strength.value > 1e-4,
      radius: Math.max(1e-3, Number(cfg.radius ?? 0.6)),
      fade: Math.max(1e-3, Number(cfg.fade ?? 0.3)),
      strength: this._strength.value,
      camPos: [c.x, c.y, c.z],
      headPos: [h.x, h.y, h.z],
    }
  }

  /** FOLLOW THE STORE, NOT WHOEVER DRAWS THE ENVIRONMENT.
   *
   *  The config and the per-frame feed used to be installed by
   *  `ThreeEnvironmentSplat`, the component that draws the environment when the
   *  SCENE owns it. A splat character's environment is owned by the avatar's own
   *  mesh instead (`environmentOwner`), and that component is then not mounted at
   *  all — so nothing configured this, `readParams().active` was false forever,
   *  and the bridge's compute pass returned on its first line every frame. The
   *  effect was fully implemented on both paths and driven on neither.
   *
   *  Both halves are owner-independent facts — there is one camera, one head and
   *  one `liveTuneStore.camera.seeThrough` — so they are installed here, once, by
   *  the module that owns the parameters. Nothing renders as a result of this;
   *  whoever draws reads `readParams()` when it draws.
   *
   *  Idempotent; returns a detach function (test seam / teardown). */
  attach({ store = liveTuneStore, controller = cameraController } = {}) {
    if (this._detach) return this._detach
    const pull = () => {
      const next = store.getState()?.camera?.seeThrough ?? null
      if (next === this._config) return
      this.configure(next)
    }
    pull()
    const unsubscribe = store.subscribe(pull)
    controller.setSeeThroughSink((camPos, headPos, strength) => this.update(camPos, headPos, strength))
    this._detach = () => {
      unsubscribe()
      controller.setSeeThroughSink(null)
      this._detach = null
      this.uninstall()
    }
    return this._detach
  }

  _report(state) {
    if (typeof window !== 'undefined') {
      const c = this._config
      window.__CAMERA_SEE_THROUGH__ = {
        state,
        enabled: !!c?.enabled,
        auto: !!c?.auto,
        radius: c?.radius ?? null,
        fade: c?.fade ?? null,
      }
    }
  }
}

export const cameraSeeThrough = new CameraSeeThrough()

// At import, not at mount: every renderer of an environment imports this module
// (the scene's `ThreeEnvironmentSplat` and the avatar's `ThreeSplatBridge`), and
// tying the wiring to one of them is exactly the bug above.
cameraSeeThrough.attach()

// Debug/test handle — drive update() directly and inspect the installed mesh
// without going through the render loop (diagnosing the follow/regenerate path).
if (typeof window !== 'undefined') {
  window.__CAMERA_SEE_THROUGH_CTL__ = cameraSeeThrough
}
